import React, { isValidElement } from 'react';
import classNames from 'classnames';

import useWindowDimensions from '../../hooks/useWindowDimensions';
import useElementDimensions from '../../hooks/useElementDimensions';

import { isElementOfType, sanitizeFragments } from '../../utilities/components';

import WpDropdownMenuItem from './WpDropdownMenuItem';
import WpDrawer from '../drawer/WpDrawer';
import WpPopoverOverlay from '../popover/WpPopoverOverlay';

import './WpDropdownMenu.scss';

export enum WidthType {
  fitWidth = 'fitWidth',
  target = 'target',
}
const DEFAULT_WIDTH_TYPE = WidthType.fitWidth;

export interface Props {
  children?: React.ReactNode;
  isOpen?: boolean;
  target?: React.Ref<HTMLAnchorElement>;
  className?: string;
  enableMobileDrawer?: boolean;
  toggle?: Function;
  width?: WidthType;
  fitHeight?: boolean;
  withMaxHeight?: boolean;
  components?: {
    footer?: Function;
  };
  popoverContext?: any;
}

const WpDropdownMenu: React.FC<Props> = ({
  children,
  components,
  toggle,
  target,
  isOpen = false,
  className,
  enableMobileDrawer = true,
  width = DEFAULT_WIDTH_TYPE,
  fitHeight = false,
  withMaxHeight = true,
  popoverContext,
}: Props) => {
  const { width: windowWidth } = useWindowDimensions();
  const { width: elementWidth } = useElementDimensions({ elementRef: target });

  const isMobile = windowWidth < 577;
  if (!isOpen) return null;

  const showAsDrawer = isMobile && enableMobileDrawer;
  const sanitizedChildren = children && sanitizeFragments(children);
  const classNamePrefix = 'WpDropdownMenu';
  const classes = classNames(
    classNamePrefix,
    className,
    fitHeight ? `${classNamePrefix}--fitHeight` : false,
    withMaxHeight ? `${classNamePrefix}--withMaxHeight` : false
  );

  const createMenu = () => {
    const widthSize = showAsDrawer || width === 'fitWidth' ? null : elementWidth;
    return (
      <ul className={classes} style={{ width: widthSize, minWidth: widthSize }}>
        {React.Children.map(sanitizedChildren, (child: React.ReactElement<any>) => {
          const isValid = isValidElement(child);
          if (child && isElementOfType(child, WpDropdownMenuItem)) {
            return React.cloneElement(child, { toggle, isInDrawer: showAsDrawer });
          } else if (isValid) {
            return React.cloneElement(child);
          } else return null;
        })}
      </ul>
    );
  };

  const createFotoer = (): React.ReactNode | null => {
    if (!components?.footer) return null;

    return <div className={`${classNamePrefix}__footer`}>{components.footer()}</div>;
  };

  if (!isOpen) return null;

  return (
    <>
      {showAsDrawer ? (
        <WpDrawer
          isOpen={isOpen}
          onToggle={() => {
            toggle();
          }}
          zIndex={1061}
          context={popoverContext}
        >
          {createMenu()}
          {createFotoer()}
        </WpDrawer>
      ) : (
        <WpPopoverOverlay context={popoverContext}>
          {createMenu()}
          {createFotoer()}
        </WpPopoverOverlay>
      )}
    </>
  );
};

export default WpDropdownMenu;
